"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PublicacionLineasCatalogoSchema = exports.PublicacionLineaCatalogoSchema = exports.PeriodoSchema = exports.LINEAS_NUEVA_PUBLICACION_SOLO_PRINCIPAL = exports.MARGEN_ANIOS_PUBLICACION = void 0;
exports.cruzaVentana = cruzaVentana;
exports.unidadesParaPublicacionNueva = unidadesParaPublicacionNueva;
exports.opcionesLineaPublicacion = opcionesLineaPublicacion;
const zod_1 = require("zod");
const estructura_institucional_1 = require("./estructura-institucional");
// ─── Publication line options: window rule and attribution unit (PUA-03..PUA-09) ──
//
// Pure rules shared by the API (validation on save) and the web (per-row options). Nothing here reads a
// clock or the database: the catalog and the current year come in as arguments, so both sides evaluate
// the same code.
//
// A line is eligible for a publication when its period overlaps the publication year +- a margin,
// compared by calendar year and inclusive at both ends. A line offered through a centro must ALSO have
// an adscription (`lineas_centro`) crossing the window. The year is the publication year
// (`publicacion.año_publicacion`); no other date takes part (PUA-08).
// ──────────────────────────────────────────────────────────────────────────────
/** Years on each side of the publication year that still count (PUA-08: one code constant). */
exports.MARGEN_ANIOS_PUBLICACION = 1;
/**
 * PUA-09 switch. `false`: a NEW attribution row may use lines of every current unit of the researcher.
 * `true`: only the principal unit's lines. Pending an institutional confirmation: flipping this constant
 * is the whole revert. Existing rows are never affected by it.
 */
exports.LINEAS_NUEVA_PUBLICACION_SOLO_PRINCIPAL = false;
/** A period in calendar years, both ends inclusive; `hasta: null` means open-ended. */
exports.PeriodoSchema = zod_1.z.object({
    desde: zod_1.z.number().int(),
    hasta: zod_1.z.number().int().nullable(),
});
const NullableString = zod_1.z.string().nullable();
/** One line of the catalog with the dates already reduced to years. */
exports.PublicacionLineaCatalogoSchema = zod_1.z.object({
    idLinea: zod_1.z.number().int(),
    nombreLinea: NullableString,
    grupoLinea: NullableString.optional().default(null),
    /** Owner unit; `null` for a transversal line (98, 99). */
    idUnidad: zod_1.z.number().int().nullable(),
    nombreUnidad: NullableString.optional().default(null),
    nombreUnidadAbreviado: NullableString.optional().default(null),
    periodo: exports.PeriodoSchema,
    /** `lineas_centro` adscriptions of the line (one entry per adscription period). */
    centros: zod_1.z
        .array(zod_1.z.object({ idCentro: zod_1.z.number().int(), periodo: exports.PeriodoSchema }))
        .optional()
        .default([]),
});
exports.PublicacionLineasCatalogoSchema = zod_1.z.object({
    /** Ids of the researcher's ACTIVE units, principal first. */
    unidadesActuales: zod_1.z.array(zod_1.z.number().int()),
    lineas: zod_1.z.array(exports.PublicacionLineaCatalogoSchema),
});
/** True when `periodo` overlaps `[anio - margen, anio + margen]`, by calendar year. */
function cruzaVentana(periodo, anio, margen = exports.MARGEN_ANIOS_PUBLICACION) {
    return periodo.desde <= anio + margen && (periodo.hasta === null || periodo.hasta >= anio - margen);
}
/**
 * Units whose lines a NEW attribution row may use (PUA-04, PUA-09): every current unit by default, only
 * the principal when the switch says so. The ONLY place that encodes "all current units".
 */
function unidadesParaPublicacionNueva(catalogo, opts = {}) {
    const soloPrincipal = opts.soloPrincipal ?? exports.LINEAS_NUEVA_PUBLICACION_SOLO_PRINCIPAL;
    return soloPrincipal ? catalogo.unidadesActuales.slice(0, 1) : [...catalogo.unidadesActuales];
}
const finMenor = (a, b) => {
    if (a === null)
        return b;
    if (b === null)
        return a;
    return Math.min(a, b);
};
const finMayor = (a, b) => {
    if (a === null || b === null)
        return null;
    return Math.max(a, b);
};
/**
 * Line options for one publication of a researcher (PUA-03, PUA-04, PUA-05, PUA-06).
 * With a row, only the lines of THAT row's unit; without one, the lines of every current unit
 * (`unidadesParaPublicacionNueva`). Transversal 98 obeys the window, "No Tributa" (99) is always offered,
 * and the currently assigned line is kept even when it is no longer eligible.
 * Without a year (`anio: null`) no unit line is eligible: only transversals (whatever their period, since the
 * window is undefined) and "No Tributa"; an assigned unit line is kept as out-of-window.
 */
function opcionesLineaPublicacion(entrada) {
    const { catalogo, anio, anioActual, idUnidadFila = null, idLineaAsignada = null } = entrada;
    const margen = entrada.margen ?? exports.MARGEN_ANIOS_PUBLICACION;
    const unidades = idUnidadFila !== null
        ? [idUnidadFila]
        : unidadesParaPublicacionNueva(catalogo, { soloPrincipal: entrada.soloPrincipal });
    // "Otra" tributes no lines: a researcher whose only unit is "Otra" can attribute only to "No Tributa".
    const soloNoTributa = unidades.length > 0 && unidades.every((idUnidad) => (0, estructura_institucional_1.esUnidadOtra)(idUnidad));
    /** How the line is reached from `unidades`, ignoring the window when `ignorarVentana`. */
    const alcance = (linea, ignorarVentana) => {
        const cruza = (periodo) => ignorarVentana || (anio !== null && cruzaVentana(periodo, anio, margen));
        const lineaCruza = cruza(linea.periodo);
        if (linea.idUnidad === null) {
            // Without a year the window is undefined: transversals are the only lines that stay eligible.
            return lineaCruza || anio === null ? { idUnidadAtribucion: null, fin: linea.periodo.hasta, porCentro: false } : null;
        }
        if (!lineaCruza)
            return null;
        // Owner path first: it wins when the line is reachable both ways. A centro that OWNS lines (e.g. unit 16)
        // also lands here, so its own lines do not need a `lineas_centro` adscription window; only lines it
        // borrows from another unit go through the centro path below.
        if (unidades.includes(linea.idUnidad)) {
            return { idUnidadAtribucion: linea.idUnidad, fin: linea.periodo.hasta, porCentro: false };
        }
        for (const idCentro of unidades) {
            const cruzando = linea.centros.filter((c) => c.idCentro === idCentro && cruza(c.periodo));
            if (cruzando.length === 0)
                continue;
            let finCentro = cruzando[0].periodo.hasta;
            for (const c of cruzando.slice(1))
                finCentro = finMayor(finCentro, c.periodo.hasta);
            return { idUnidadAtribucion: idCentro, fin: finMenor(linea.periodo.hasta, finCentro), porCentro: true };
        }
        return null;
    };
    const resultado = [];
    const vistas = new Set();
    for (const linea of catalogo.lineas) {
        if (vistas.has(linea.idLinea))
            continue;
        const esNoTributa = linea.idLinea === estructura_institucional_1.LINEA_NO_TRIBUTA_ID;
        let hallado = null;
        if (esNoTributa) {
            // Always offered, whatever the year (PUA-03).
            hallado = { idUnidadAtribucion: null, fin: linea.periodo.hasta, porCentro: false };
        }
        else if (!soloNoTributa) {
            hallado = alcance(linea, false);
        }
        if (hallado) {
            vistas.add(linea.idLinea);
            resultado.push({ linea, alcance: hallado, fuera: false });
        }
        else if (idLineaAsignada !== null && linea.idLinea === idLineaAsignada) {
            // Held but not eligible: keep it, with the unit it would be attributed to (if any).
            vistas.add(linea.idLinea);
            resultado.push({
                linea,
                alcance: alcance(linea, true) ?? { idUnidadAtribucion: null, fin: linea.periodo.hasta, porCentro: false },
                fuera: true,
            });
        }
    }
    const unidadesDuenas = new Set();
    for (const { linea } of resultado)
        if (linea.idUnidad !== null)
            unidadesDuenas.add(linea.idUnidad);
    const mostrarUnidad = unidadesDuenas.size > 1 || resultado.some((r) => r.alcance.porCentro);
    return resultado.map(({ linea, alcance: a, fuera }) => ({
        idLinea: linea.idLinea,
        nombreLinea: linea.nombreLinea,
        grupoLinea: linea.grupoLinea,
        idUnidadDuena: linea.idUnidad,
        nombreUnidad: linea.nombreUnidad,
        nombreUnidadAbreviado: linea.nombreUnidadAbreviado,
        idUnidadAtribucion: a.idUnidadAtribucion,
        // "No Tributa" is always offered, so it never carries an end-year label.
        vigenteHastaAnio: linea.idLinea !== estructura_institucional_1.LINEA_NO_TRIBUTA_ID && a.fin !== null && a.fin < anioActual ? a.fin : null,
        asignadaFueraDeVentana: fuera,
        mostrarUnidad,
    }));
}
